import React, { PropsWithChildren, createContext, useContext, useMemo } from 'react'
import useBoolean from './useBoolean'

interface AppContextProps {
  isRightSideBar: boolean
  openRightSideBar: () => void
  closeRightSideBar: () => void
  toggleRightSideBar: (value: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => void
  isNotification: boolean
  openNotification: () => void
  closeNotification: () => void
  toggleNotification: (value: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => void
}

const AppContext = createContext<AppContextProps>({
  isRightSideBar: true,
  openRightSideBar: () => {},
  closeRightSideBar: () => {},
  toggleRightSideBar: () => () => {},
  isNotification: false,
  openNotification: () => {},
  closeNotification: () => {},
  toggleNotification: () => () => {}
})

export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const isRightSideBar = useBoolean(true)
  const isNotification = useBoolean(false)

  const toggleRightSideBar = (value: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event && event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return
    }

    isRightSideBar.setValue(value)
  }

  const toggleNotification = (value: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event && event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return
    }

    isNotification.setValue(value)
  }

  const value = useMemo(
    () => ({
      isRightSideBar: isRightSideBar.value,
      openRightSideBar: isRightSideBar.setTrue,
      closeRightSideBar: isRightSideBar.setFalse,
      toggleRightSideBar,
      isNotification: isNotification.value,
      openNotification: isNotification.setTrue,
      closeNotification: isNotification.setFalse,
      toggleNotification
    }),
    [isNotification, isRightSideBar]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export const useApp = () => {
  return useContext(AppContext)
}
